import React, { type ReactNode } from "react";
import { useUser } from "../state";
import { DateTimeInput } from "../DesignSystem/DateTimeInput";
import { TestIds } from "../testIds";
import { TrophyIcon } from "@heroicons/react/24/outline";

const Card = ({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={`rounded-2xl border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-slate-800 ${className}`}
  >
    {children}
  </div>
);

const CardTitle = ({
  children,
  icon,
}: {
  children: ReactNode;
  icon?: ReactNode;
}) => (
  <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-white">
    {icon && <span className="text-slate-500 dark:text-slate-400">{icon}</span>}
    {children}
  </h3>
);

const FormField = ({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) => (
  <div className="space-y-1.5">
    <label className="block text-sm font-medium text-slate-600 dark:text-slate-300">
      {label}
    </label>
    {children}
    {hint && (
      <p className="text-xs text-slate-500 dark:text-slate-400">{hint}</p>
    )}
  </div>
);

const inputClassName =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-white";

const textareaClassName = `${inputClassName} resize-y min-h-[80px]`;

const checkboxClassName =
  "rounded border-slate-300 bg-white text-blue-500 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:focus:ring-offset-slate-800";

export const NewCompoPage = (): JSX.Element => {
  const user = useUser();

  if (user.type !== "logged-in" || !user.user.admin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 dark:bg-slate-900">
        <div className="text-slate-500 dark:text-slate-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 px-4 pb-8 pt-16 dark:bg-slate-900">
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 dark:text-white">
            Add New Compo
          </h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Create a new competition
          </p>
        </div>

        <Card>
          <CardTitle icon={<TrophyIcon className="h-5 w-5" />}>
            Compo Details
          </CardTitle>
          <form
            action="/api/compo/admin/create"
            method="post"
            className="space-y-4"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Compo Name">
                <input
                  type="text"
                  name="name"
                  data-testid={TestIds.InputCompoName}
                  className={inputClassName}
                />
              </FormField>

              <FormField label="Theme Name">
                <input
                  type="text"
                  name="theme_short"
                  data-testid={TestIds.InputThemeShort}
                  className={inputClassName}
                />
              </FormField>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <FormField label="Start Time">
                <DateTimeInput
                  name="start"
                  inputClassName={inputClassName}
                  dataTestId={TestIds.DateInputStart}
                />
              </FormField>

              <FormField
                label="Judging Start / Compo End"
                hint="A sync listen will be scheduled at this time"
              >
                <DateTimeInput
                  name="stop"
                  inputClassName={inputClassName}
                  dataTestId={TestIds.DateInputStop}
                />
              </FormField>

              <FormField label="Judging End">
                <DateTimeInput
                  name="judging_stop"
                  inputClassName={inputClassName}
                  dataTestId={TestIds.DateInputJudgingStop}
                />
              </FormField>
            </div>

            <FormField label="Theme Description">
              <textarea
                name="theme"
                defaultValue=""
                placeholder="Describe the theme in detail..."
                data-testid={TestIds.InputThemeDescription}
                className={textareaClassName}
              />
            </FormField>

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Primary Image">
                <input
                  type="text"
                  name="primary_image"
                  placeholder="https://example.com/image.png"
                  defaultValue=""
                  data-testid={TestIds.InputPrimaryImage}
                  className={inputClassName}
                />
              </FormField>

              <FormField label="Header Image">
                <input
                  type="text"
                  name="header_image"
                  placeholder="https://example.com/header.png"
                  defaultValue=""
                  data-testid={TestIds.InputHeaderImage}
                  className={inputClassName}
                />
              </FormField>
            </div>

            <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-700/30">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  name="allow_voting"
                  defaultChecked={true}
                  className={checkboxClassName}
                />
                <span className="text-sm text-slate-700 dark:text-slate-200">
                  Allow voting on themes
                </span>
              </label>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  name="hide_images_before_compo_start"
                  defaultChecked={true}
                  className={checkboxClassName}
                />
                <span className="text-sm text-slate-700 dark:text-slate-200">
                  Hide images before compo starts
                </span>
              </label>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                data-testid={TestIds.SubmitNewCompo}
                className="rounded-lg bg-blue-600 px-6 py-2.5 font-medium text-white transition hover:bg-blue-700"
              >
                Create Compo
              </button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};
